import React, { useCallback, useRef, useState } from "react";
import { Box, Stack, styled } from "@mui/material";
import { useLearnosityPlayer } from "../hooks/useLearnosityPlayer";
import { LadderActivityLoading } from "./LadderActivityLoading";
import { ExitButton } from "./ExitButton";
import { LadderTitle } from "./LadderTitle";
import { getReportingFields, isGameLadderType } from "../utils/ladder-util";
import type {
  ActivityResult,
  CompletionDisplayData,
  Ladder,
  LearnosityInitOptions,
} from "../types";

const LadderTitleSpot = styled(Box)(() => ({
  position: "absolute",
  top: "19px",
  left: "110px",
  right: "110px",
}));

const AssessmentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isGame",
})<{ isGame?: boolean }>(({ theme, isGame }) => ({
  width: "90%",
  marginTop: isGame ? "0%" : "5%",
  marginLeft: "5%",
  marginRight: "5%",
  [theme.breakpoints.down("sm")]: {
    marginTop: "100px",
  },
}));

interface LadderAssessmentViewProps {
  ladder: Ladder;
  learnosityInitOptions: LearnosityInitOptions | null;
  sessionId: string | null;
  onExit: () => void;
  onActivityStarted: (itemsApi: unknown) => void;
  onActivitySubmitted: (result: ActivityResult) => Promise<CompletionDisplayData>;
  onSubmissionComplete: (data: CompletionDisplayData) => void;
}

export const LadderAssessmentView = ({
  ladder,
  learnosityInitOptions,
  sessionId,
  onExit,
  onActivityStarted,
  onActivitySubmitted,
  onSubmissionComplete,
}: LadderAssessmentViewProps) => {
  const [submitting, setSubmitting] = useState(false);
  const onActivityStartedRef = useRef(onActivityStarted);
  onActivityStartedRef.current = onActivityStarted;
  const onActivitySubmittedRef = useRef(onActivitySubmitted);
  onActivitySubmittedRef.current = onActivitySubmitted;
  const onSubmissionCompleteRef = useRef(onSubmissionComplete);
  onSubmissionCompleteRef.current = onSubmissionComplete;

  const isGameLadder = isGameLadderType(ladder.ladderType);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleActivityStarted = useCallback((itemsApi: any) => {
    onActivityStartedRef.current(itemsApi);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmitted = useCallback(async (itemsApi: any) => {
    try {
      setSubmitting(true);
      const scores = itemsApi.getScores();
      const responses = itemsApi.getResponses();
      const tags = itemsApi.getTags();
      const items = itemsApi.getItems();
      const time = itemsApi.getTime();

      const result: ActivityResult = {
        scores,
        responses,
        tags,
        items,
        time,
        sessionId: itemsApi.forSessionId || "",
        reportingFields: getReportingFields(itemsApi),
      };

      const completionData = await onActivitySubmittedRef.current(result);

      // Hide the learnosity div
      const el = document.getElementById(divId);
      if (el) el.style.display = "none";

      onSubmissionCompleteRef.current(completionData);
    } catch (error) {
      console.error("Error onActivitySubmitted", error);
    } finally {
      setSubmitting(false);
    }
  }, []);

  const {
    divId,
    loading: activityLoading,
    itemsApi,
    error: hadError,
  } = useLearnosityPlayer({
    learnosityInitOptions,
    sessionId,
    events: {
      "test:start": handleActivityStarted,
      "test:resume": handleActivityStarted,
      "test:finished:submit": handleSubmitted,
    },
    ladderType: ladder.ladderType,
  });

  const onExitButton = () => {
    if (itemsApi) {
      if (itemsApi.save) itemsApi.save();
    }
    onExit();
  };

  if (hadError) {
    return <div>Error loading assessment</div>;
  }

  return (
    <>
      <Stack sx={{ paddingTop: "20px", paddingLeft: "10px" }}>
        <ExitButton onClick={onExitButton} />
      </Stack>
      <LadderTitleSpot>
        <LadderTitle ladder={ladder} />
      </LadderTitleSpot>
      <AssessmentContainer
        isGame={isGameLadder}
        className={`ladder-assessment-container ${ladder.ladderType} ladder-id-${ladder.id}`}
      >
        <div
          id={divId}
          style={{
            width: "100%",
          }}
        />
      </AssessmentContainer>
      {(activityLoading || submitting) && (
        <LadderActivityLoading label={submitting ? "Saving Results" : "Loading Assessment"} />
      )}
    </>
  );
};
